import { useState, useRef, useCallback } from "react";

import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useAuth, authorizedAxios, useProfile } from "@moonscape/shared";

export const useImportFile = () => {
  const queryClient = useQueryClient();
  const { profile } = useProfile();
  const { getToken } = useAuth();

  // Use refs to avoid state timing issues
  const fileRef = useRef<File | null>(null);
  const exchangeIdRef = useRef<string | null>(null);

  // Keep state for UI reactivity
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [exchangeId, setExchangeId] = useState<string | null>(null);

  const importFile = async () => {
    // Use refs which are synchronously updated
    const file = fileRef.current;
    const exId = exchangeIdRef.current;

    console.log("[useImportFile] importFile called, file:", file?.name, "exchangeId:", exId);

    if (!file) {
      console.error("[useImportFile] No file in ref");
      throw new Error("No file selected");
    }

    console.log("[useImportFile] Starting upload for:", exId, file?.name, file?.size);

    let token;
    try {
      token = await getToken();
      console.log("[useImportFile] Got auth token");
    } catch (authError) {
      console.error("[useImportFile] Failed to get auth token:", authError);
      throw new Error("Authentication failed. Please try logging in again.");
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("exchangeId", exId || "");

    console.log("[useImportFile] FormData created, sending request...");

    try {
      const res = await authorizedAxios(token).post(
        "/import/file/transactions",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 300000, // 5 minute timeout for large CSV files
        }
      );
      console.log("[useImportFile] Upload successful:", res.status);
      if (profile?.isDemo && queryClient) {
        queryClient.invalidateQueries();
        console.log("[useImportFile] queryClient.invalidateQueries() called");
      }
      return res;
    } catch (uploadError: any) {
      console.error("[useImportFile] Upload failed:", {
        message: uploadError?.message,
        code: uploadError?.code,
        status: uploadError?.response?.status,
        data: uploadError?.response?.data,
      });
      throw uploadError;
    }
  };

  const mutation = useMutation({
    mutationFn: importFile,
    mutationKey: ["importFile"],
  });

  const setExchangeIdAndFile = useCallback((newExchangeId: string, file: File) => {
    // Update refs synchronously - these will be available immediately
    fileRef.current = file;
    exchangeIdRef.current = newExchangeId;

    // Also update state for UI reactivity
    setExchangeId(newExchangeId);
    setSelectedFile(file);

    console.log("[useImportFile] setExchangeIdAndFile called:", newExchangeId, file?.name);
  }, []);

  const upload = useCallback(async () => {
    console.log("[useImportFile] upload called, fileRef:", fileRef.current?.name);
    return mutation.mutateAsync();
  }, [mutation]);

  return {
    isLoading: mutation.isPending,
    data: mutation.data,
    error: mutation.error,
    setExchangeIdAndFile,
    upload,
  };
};
